import React, { useState, useEffect } from 'react';
import InsuranceVerifyPage from './InsuranceVerifyPage';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';

// Insurance Verify legacy hub key opens InsurancePage on the Verify tab.
const LEGACY_TAB_MAP = { 'insurance-verify': 'verify' };

const InsurancePage = ({ API_BASE, patientsList, currentUser, initialTab = 'claims' }) => {
  const [tab, setTab] = useState(LEGACY_TAB_MAP[initialTab] || initialTab);
  const [claims, setClaims] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState({});
  const [claimForm, setClaimForm] = useState({ patientId:'', companyId:'', policyNumber:'', preAuthAmount:'', claimAmount:'', notes:'' });
  const [compForm, setCompForm] = useState({ name:'', tpaName:'', contactName:'', phone:'', email:'' });
  const [editComp, setEditComp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchAll = async () => {
    try {
      const [cl, co, su] = await Promise.all([
        axios.get(`${API_BASE}/insurance/claims${statusFilter ? `?claimStatus=${statusFilter}` : ''}`),
        axios.get(`${API_BASE}/insurance/companies?active=true`),
        axios.get(`${API_BASE}/insurance/claims/summary`),
      ]);
      setClaims(cl.data); setCompanies(co.data); setSummary(su.data);
    } catch(e){}
  };

  useEffect(() => { fetchAll(); }, [statusFilter]); // eslint-disable-line

  const handleCreateClaim = async () => {
    if (!claimForm.patientId) return alert('Select a patient');
    try {
      await axios.post(`${API_BASE}/insurance/claims`, {
        ...claimForm,
        preAuthAmount: Number(claimForm.preAuthAmount) || 0,
        claimAmount: Number(claimForm.claimAmount) || 0,
      });
      setClaimForm({ patientId:'', companyId:'', policyNumber:'', preAuthAmount:'', claimAmount:'', notes:'' });
      setTab('claims'); fetchAll();
    } catch(e){ alert('Failed'); }
  };

  const handleAutoFill = async () => {
    if (!claimForm.patientId) return alert('Select a patient first — AI will pull their latest bill + treatments.');
    try {
      const { data } = await axios.get(`${API_BASE}/insurance/claims/autofill/${claimForm.patientId}`);
      setClaimForm(prev => ({
        ...prev,
        companyId: data.companyId || prev.companyId,
        policyNumber: data.policyNumber || prev.policyNumber,
        preAuthAmount: data.preAuthAmount || prev.preAuthAmount,
        claimAmount: data.claimAmount || prev.claimAmount,
        notes: [
          data.diagnosis ? `Dx: ${data.diagnosis}` : '',
          data.procedures?.length ? `Procedures: ${data.procedures.map(p => p.name).join(', ')}` : '',
          data.invoiceNo ? `Invoice: ${data.invoiceNo}` : '',
        ].filter(Boolean).join(' | '),
      }));
      alert(`Auto-filled from ${data.invoiceNo || 'patient records'} — verify then submit.`);
    } catch (e) { alert('Auto-fill failed: ' + (e.response?.data?.error || e.message)); }
  };

  const handleUpdateStatus = async (id, updates) => {
    await axios.put(`${API_BASE}/insurance/claims/${id}`, updates);
    fetchAll();
  };

  const handleSaveComp = async () => {
    if (!compForm.name.trim()) return alert('Name required');
    try {
      if (editComp) await axios.put(`${API_BASE}/insurance/companies/${editComp._id}`, compForm);
      else          await axios.post(`${API_BASE}/insurance/companies`, compForm);
      setCompForm({ name:'', tpaName:'', contactName:'', phone:'', email:'' }); setEditComp(null); fetchAll();
    } catch(e){ alert('Failed'); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const fmtINR  = n => `₹${(n||0).toLocaleString('en-IN')}`;

  const claimStatusStyles = {
    draft:        { bg:'#f3f4f6', color:'#374151' },
    submitted:    { bg:'#dbeafe', color:'#1e40af' },
    'under-review':{ bg:'#fef3c7', color:'#92400e' },
    settled:      { bg:'#d1fae5', color:'#065f46' },
    denied:       { bg:'#fee2e2', color:'#991b1b' },
  };

  const tabBtn = (key, label) => (
    <button onClick={()=>setTab(key)}
      style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
        backgroundColor: tab===key ? theme.primary : '#e9ecef', color: tab===key ? 'white' : '#555'}}>
      {label}
    </button>
  );

  return (
    <div>
      {/* KPI */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px', marginBottom:'20px'}}>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Submitted</span>
          <h2 style={{margin:'4px 0 0', color:theme.info, fontSize:'1.4rem', fontWeight:'700'}}>{(summary.submitted||0) + (summary.underReview||0)}</h2></div>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Settled</span>
          <h2 style={{margin:'4px 0 0', color:theme.success, fontSize:'1.4rem', fontWeight:'700'}}>{summary.settled||0}</h2></div>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Denied</span>
          <h2 style={{margin:'4px 0 0', color:theme.danger, fontSize:'1.4rem', fontWeight:'700'}}>{summary.denied||0}</h2></div>
        <div style={statCard}><span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total Settled</span>
          <h2 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>{fmtINR(summary.totalSettled)}</h2></div>
      </div>

      <div style={{display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap'}}>
        {tabBtn('claims', 'Claims')}
        {tabBtn('create', '+ New Claim')}
        {tabBtn('companies', 'Insurance Companies')}
        {tabBtn('verify', 'Pre-Auth Verify')}
      </div>

      {/* VERIFY — InsuranceVerifyPage embedded as a tab */}
      {tab === 'verify' && <InsuranceVerifyPage API_BASE={API_BASE} currentUser={currentUser} />}

      {/* Claims */}
      {tab === 'claims' && (
        <div style={cardStyle}>
          <div style={{display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap'}}>
            <span style={{fontSize:'0.82rem', color:theme.textMuted, fontWeight:'600', alignSelf:'center'}}>Filter:</span>
            {['','draft','submitted','under-review','settled','denied'].map(s => (
              <button key={s} onClick={()=>setStatusFilter(s)}
                style={{padding:'5px 14px', borderRadius:'14px', border:'1px solid #dee2e6', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600',
                  backgroundColor: statusFilter===s ? theme.primary : 'white', color: statusFilter===s ? 'white' : '#555'}}>
                {s || 'All'}
              </button>
            ))}
          </div>
          {claims.length === 0 ? <p style={{color:'#aaa', textAlign:'center', padding:'40px'}}>No claims found</p> : (
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead><tr>
                <th style={thStyle}>Patient</th><th style={thStyle}>Company</th><th style={thStyle}>Policy #</th>
                <th style={thStyle}>Claim Amt</th><th style={thStyle}>Settled</th><th style={thStyle}>Status</th><th style={thStyle}>Actions</th>
              </tr></thead>
              <tbody>{claims.map(c => {
                const ss = claimStatusStyles[c.claimStatus] || claimStatusStyles.draft;
                return (
                  <tr key={c._id}>
                    <td style={tdStyle}><strong>{c.patientId?.name||'—'}</strong></td>
                    <td style={tdStyle}>{c.companyId?.name||'—'}</td>
                    <td style={tdStyle}>{c.policyNumber||'—'}</td>
                    <td style={tdStyle}>{fmtINR(c.claimAmount)}</td>
                    <td style={tdStyle}>{fmtINR(c.settledAmount)}</td>
                    <td style={tdStyle}><span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700', backgroundColor:ss.bg, color:ss.color}}>{c.claimStatus}</span></td>
                    <td style={tdStyle}>
                      <div style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                        {c.claimStatus==='draft' && <button onClick={()=>handleUpdateStatus(c._id,{claimStatus:'submitted',claimDate:new Date()})} style={{padding:'3px 8px', backgroundColor:theme.info, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'600'}}>Submit</button>}
                        {c.claimStatus==='submitted' && <button onClick={()=>handleUpdateStatus(c._id,{claimStatus:'under-review'})} style={{padding:'3px 8px', backgroundColor:theme.warning, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'600'}}>Under Review</button>}
                        {(c.claimStatus==='submitted'||c.claimStatus==='under-review') && <>
                          <button onClick={()=>{const amt=prompt('Settled amount:',c.claimAmount); if(amt) handleUpdateStatus(c._id,{claimStatus:'settled',settledAmount:Number(amt),settledDate:new Date()});}} style={{padding:'3px 8px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'600'}}>Settle</button>
                          <button onClick={()=>{const reason=prompt('Denial reason:'); if(reason) handleUpdateStatus(c._id,{claimStatus:'denied',denialReason:reason});}} style={{padding:'3px 8px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.7rem', fontWeight:'600'}}>Deny</button>
                        </>}
                      </div>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </div>
      )}

      {/* Create Claim */}
      {tab === 'create' && (
        <div style={{...cardStyle, borderTop:`4px solid ${theme.info}`}}>
          <h3 style={{margin:'0 0 16px', color:theme.navy}}>New Insurance Claim</h3>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'14px'}}>
            <div><label style={labelStyle}>Patient *</label>
              <select value={claimForm.patientId} onChange={e=>setClaimForm({...claimForm, patientId:e.target.value})} style={inputStyle}>
                <option value="">Select...</option>
                {patientsList.map(p => <option key={p._id} value={p._id}>{p.name} ({p.phone})</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Insurance Company</label>
              <select value={claimForm.companyId} onChange={e=>setClaimForm({...claimForm, companyId:e.target.value})} style={inputStyle}>
                <option value="">Select...</option>
                {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Policy Number</label><input value={claimForm.policyNumber} onChange={e=>setClaimForm({...claimForm, policyNumber:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Pre-Auth Amount ₹</label><input type="number" value={claimForm.preAuthAmount} onChange={e=>setClaimForm({...claimForm, preAuthAmount:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Claim Amount ₹</label><input type="number" value={claimForm.claimAmount} onChange={e=>setClaimForm({...claimForm, claimAmount:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Notes</label><input value={claimForm.notes} onChange={e=>setClaimForm({...claimForm, notes:e.target.value})} style={inputStyle}/></div>
          </div>
          <div style={{display:'flex', gap:'10px'}}>
            <button onClick={handleAutoFill} style={{...btnStyle, marginTop:0, backgroundColor:theme.info}}>🪄 AI Auto-Fill from Latest Bill</button>
            <button onClick={handleCreateClaim} style={{...btnStyle, marginTop:0}}>Create Claim</button>
          </div>
        </div>
      )}

      {/* Companies */}
      {tab === 'companies' && (
        <div style={cardStyle}>
          <div style={{backgroundColor:'#f8f9fa', borderRadius:'10px', padding:'18px', marginBottom:'20px'}}>
            <h4 style={{color:'#0582ca', margin:'0 0 14px'}}>{editComp ? 'Edit Company' : '+ Add Company'}</h4>
            <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
              <div><label style={labelStyle}>Name *</label><input value={compForm.name} onChange={e=>setCompForm({...compForm, name:e.target.value})} style={inputStyle}/></div>
              <div><label style={labelStyle}>TPA Name</label><input value={compForm.tpaName} onChange={e=>setCompForm({...compForm, tpaName:e.target.value})} style={inputStyle}/></div>
              <div><label style={labelStyle}>Contact</label><input value={compForm.contactName} onChange={e=>setCompForm({...compForm, contactName:e.target.value})} style={inputStyle}/></div>
              <div><label style={labelStyle}>Phone</label><input value={compForm.phone} onChange={e=>setCompForm({...compForm, phone:e.target.value})} style={inputStyle}/></div>
              <div><label style={labelStyle}>Email</label><input value={compForm.email} onChange={e=>setCompForm({...compForm, email:e.target.value})} style={inputStyle}/></div>
            </div>
            <button onClick={handleSaveComp} style={{...btnStyle, marginTop:0}}>{editComp ? 'Update' : 'Add Company'}</button>
          </div>
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>TPA</th><th style={thStyle}>Contact</th><th style={thStyle}>Phone</th><th style={thStyle}></th></tr></thead>
            <tbody>{companies.map(c => (
              <tr key={c._id}>
                <td style={tdStyle}><strong>{c.name}</strong></td>
                <td style={tdStyle}>{c.tpaName||'—'}</td>
                <td style={tdStyle}>{c.contactName||'—'}</td>
                <td style={tdStyle}>{c.phone||'—'}</td>
                <td style={tdStyle}><button onClick={()=>{setEditComp(c); setCompForm({name:c.name||'',tpaName:c.tpaName||'',contactName:c.contactName||'',phone:c.phone||'',email:c.email||''});}} style={{padding:'4px 10px', backgroundColor:'#0582ca', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Edit</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InsurancePage;
